import { jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useCallback, useMemo } from 'react';
import ReactDatePicker from 'react-datepicker';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { DatePickerHeader } from '../DatePicker.Header/DatePicker.Header.es.js';
import { DatePickerInner } from '../DateRange.Inner/DatePicker.Inner.es.js';

const DatePickerCore = /*#__PURE__*/ forwardRef(({ className, locale = {
    code: 'en'
}, dateFormat = 'dd-MM-yyyy', label, labelSuffix, display, message, error = false, disabled = false, placeholder, onSingleChange, onRangeChange, isClearable = false, selectsRange = false, noFuture, noPast, minDate, maxDate, selected, startDate, endDate, intent = 'default', name, monthsShown = 1, inline = false, ...rest }, // React-hook-form ref
ref)=>{
    const datePickerRef = useRef(null);
    const clearButtonRef = useRef(null);
    const isDateRange = useCallback((date)=>selectsRange && Array.isArray(date), [
        selectsRange
    ]);
    const handleChange = useCallback((date, event)=>{
        if (isDateRange(date)) {
            let [from, to] = date;
            // This will set 00:00 and 23:59 in the user's timezone, to avoid
            // the problem not displaying all the records from the last day.
            // If we allow the user to also select the time,
            // we may want to disable this, so we won't override
            // the time that the user selected.
            if (from instanceof Date) {
                from = DateTime.fromJSDate(from).startOf('day').toJSDate();
            }
            if (to instanceof Date) {
                to = DateTime.fromJSDate(to).endOf('day').toJSDate();
            }
            return onRangeChange?.([
                from,
                to
            ], event);
        }
        onSingleChange?.(date, event);
    }, [
        isDateRange,
        onRangeChange,
        onSingleChange
    ]);
    const calcMinDate = useMemo(()=>{
        if (noPast) {
            return new Date();
        }
        return minDate;
    }, [
        minDate,
        noPast
    ]);
    const calcMaxDate = useMemo(()=>{
        if (noFuture) {
            return new Date();
        }
        return maxDate;
    }, [
        maxDate,
        noFuture
    ]);
    const renderCustomHeader = useCallback((props)=>jsx(DatePickerHeader, {
            ...props,
            locale: locale,
            monthsShown: monthsShown
        }), [
        locale,
        monthsShown
    ]);
    const handleClearDate = useCallback((event)=>{
        event.stopPropagation();
        onRangeChange?.([
            null,
            null
        ], event);
        onSingleChange?.(null, event);
    }, [
        onRangeChange,
        onSingleChange
    ]);
    const handleOnFocus = useCallback(()=>{
        // Open on focus, unless clearing input
        if (clearButtonRef.current != null) {
            datePickerRef.current?.setOpen(true);
        }
    }, []);
    const dateSelected = useMemo(()=>{
        return selected != null || startDate != null && endDate != null;
    }, [
        selected,
        startDate,
        endDate
    ]);
    const formatWeekDay = useCallback((day)=>day.slice(0, 3), []);
    return jsx("span", {
        className: classNames('cb-date-picker', {
            label
        }, className),
        children: jsx(ReactDatePicker, {
            ref: datePickerRef,
            calendarClassName: classNames('cb-date-picker-calendar', {
                'no-shadow': inline
            }),
            customInput: jsx(DatePickerInner, {
                clearButtonRef: clearButtonRef,
                display: display,
                error: error,
                fieldRef: ref,
                handleClearDate: handleClearDate,
                intent: intent,
                isClearable: isClearable && dateSelected,
                label: label,
                labelSuffix: labelSuffix,
                locale: locale,
                message: message,
                placeholder: placeholder
            }),
            dateFormat: dateFormat,
            disabled: disabled,
            endDate: endDate,
            formatWeekDay: formatWeekDay,
            inline: inline,
            locale: locale.code,
            maxDate: calcMaxDate,
            minDate: calcMinDate,
            monthsShown: monthsShown,
            name: name,
            onChange: handleChange,
            onFocus: handleOnFocus,
            placeholderText: placeholder,
            renderCustomHeader: renderCustomHeader,
            selected: selected,
            selectsRange: selectsRange,
            startDate: startDate,
            preventOpenOnFocus: true,
            ...rest
        })
    });
});
DatePickerCore.displayName = 'DatePicker.Core';

export { DatePickerCore };
//# sourceMappingURL=DatePicker.Core.es.js.map
